/**
 * ui_nav.ts — spatial focus navigation, so the windows and menus can be driven
 * from a gamepad's d-pad without a mouse.
 *
 * The geometry is PURE (`pickNext`), which is the part worth testing: given the
 * rectangles of what is on screen and a direction, which control should take
 * focus? The DOM half is a thin wrapper that reads rects and calls `focus()`.
 *
 * There is no window registry in `ui_window.ts` — only a shared z counter — so
 * the topmost open panel is found by querying `[data-atlas-nav]` and taking the
 * greatest z-index among the visible ones.
 */

/** A focus candidate: its index and its on-screen rectangle. */
export interface NavRect {
  x: number; y: number; w: number; h: number;
}

const cx = (r: NavRect): number => r.x + r.w / 2;
const cy = (r: NavRect): number => r.y + r.h / 2;

/**
 * Pick the control a directional press should move to, or −1 for none.
 *
 * Scored the way every console UI does it: only candidates genuinely in that
 * direction are eligible, and among them the nearest wins with the OFF-AXIS
 * distance weighted heavily — so pressing down from a control goes to the thing
 * under it rather than to whatever happens to be closest in a straight line.
 *
 * With no current focus, the first candidate nearest the panel's top-left is the
 * entry point, which is where a reader's eye starts.
 */
export function pickNext(rects: readonly NavRect[], current: number, dx: number, dy: number): number {
  if (!rects.length) return -1;
  if (current < 0 || current >= rects.length) {
    let best = 0, bestD = Infinity;
    for (let i = 0; i < rects.length; i++) {
      const d = rects[i].y * 2 + rects[i].x;
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  }
  if (dx === 0 && dy === 0) return current;
  const from = rects[current];
  const fx = cx(from), fy = cy(from);
  let best = -1, bestScore = Infinity;
  for (let i = 0; i < rects.length; i++) {
    if (i === current) continue;
    const r = rects[i];
    const ox = cx(r) - fx, oy = cy(r) - fy;
    // Along the press, and across it.
    const along = ox * dx + oy * dy;
    const across = Math.abs(ox * dy - oy * dx);
    if (along <= 1) continue;                       // not in that direction at all
    // OFF-AXIS IS WEIGHTED: pressing down must reach the control under this one,
    // not a distant one that merely happens to be closer as the crow flies.
    const score = along + across * 3;
    if (score < bestScore) { bestScore = score; best = i; }
  }
  return best;
}

/** Elements a pad may focus. Disabled and invisible ones are never offered. */
const FOCUSABLE = 'button, [role="button"], a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function isVisible(el: HTMLElement): boolean {
  if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') return false;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return false;
  const st = getComputedStyle(el);
  return st.display !== 'none' && st.visibility !== 'hidden' && Number(st.opacity) > 0.05;
}

/** The topmost open panel a pad should be driving, or null. */
export function topNavPanel(doc: Document = document): HTMLElement | null {
  let best: HTMLElement | null = null, bestZ = -Infinity;
  for (const el of Array.from(doc.querySelectorAll<HTMLElement>('[data-atlas-nav]'))) {
    if (!isVisible(el)) continue;
    const z = Number(getComputedStyle(el).zIndex);
    const zz = Number.isFinite(z) ? z : 0;
    if (zz >= bestZ) { bestZ = zz; best = el; }
  }
  return best;
}

/** A panel's focusable list, kept until the panel's DOM changes (see `navTargets`). */
interface NavCache { els: HTMLElement[]; dirty: boolean; at: number; mo: MutationObserver | null }
const navCache = new WeakMap<HTMLElement, NavCache>();
/** A list older than this is re-read anyway: visibility can change through a stylesheet, with no mutation inside. */
const NAV_CACHE_MAX_MS = 500;
/** The attributes that can add, remove, hide or disable a target (`data-ax-focus`, the ring, is deliberately absent). */
const NAV_ATTRS = ['style', 'class', 'hidden', 'disabled', 'aria-disabled', 'tabindex', 'href', 'type', 'open'];

/**
 * Every focusable control inside a panel, in DOM order. CACHED PER PANEL (2026-09-25): the pad ticks every frame,
 * and each tick ran `querySelectorAll` plus a `getBoundingClientRect` and a `getComputedStyle` per control — a forced
 * layout per frame for as long as a window stood open. The list is kept until a MutationObserver on the panel sees a
 * child or a relevant attribute change (pending records are taken synchronously, so a re-render earlier in the same
 * frame counts), until a kept element left the document, or until it is `NAV_CACHE_MAX_MS` old. Treat it read-only.
 */
export function navTargets(panel: HTMLElement, now: number = performance.now()): readonly HTMLElement[] {
  let c = navCache.get(panel);
  if (!c) {
    const fresh: NavCache = { els: [], dirty: true, at: 0, mo: null };
    if (typeof MutationObserver !== 'undefined') {
      fresh.mo = new MutationObserver(() => { fresh.dirty = true; });
      fresh.mo.observe(panel, { subtree: true, childList: true, attributes: true, attributeFilter: NAV_ATTRS });
    }
    navCache.set(panel, fresh);
    c = fresh;
  }
  if (c.mo && c.mo.takeRecords().length > 0) c.dirty = true;
  if (c.dirty || now - c.at > NAV_CACHE_MAX_MS || c.els.some((e) => !e.isConnected)) {
    c.els = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);
    c.dirty = false;
    c.at = now;
  }
  return c.els;
}

/** Their rectangles, for `pickNext`. */
export function navRects(els: readonly HTMLElement[]): NavRect[] {
  return els.map((e) => { const r = e.getBoundingClientRect(); return { x: r.x, y: r.y, w: r.width, h: r.height }; });
}

/** Pixels per second the right stick scrolls a panel at full deflection. */
export const NAV_SCROLL_RATE = 900;

/**
 * The nearest ancestor that can actually scroll, or null. A long settings panel
 * scrolls its BODY, not the window root, so the stick has to find the element
 * that will actually move rather than the one the focus happens to sit in.
 */
export function scrollableOf(el: HTMLElement | null, panel: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el;
  while (node && node !== panel.parentElement) {
    if (node.scrollHeight - node.clientHeight > 4) {
      const st = getComputedStyle(node).overflowY;
      if (st === 'auto' || st === 'scroll' || st === 'overlay') return node;
    }
    node = node.parentElement;
  }
  // Fall back to the panel itself, or the tallest scrollable thing inside it — a
  // body that scrolls but sits beside the focus is still what the player means.
  if (panel.scrollHeight - panel.clientHeight > 4) return panel;
  let best: HTMLElement | null = null, bestOver = 0;
  for (const c of Array.from(panel.querySelectorAll<HTMLElement>('*'))) {
    const over = c.scrollHeight - c.clientHeight;
    if (over <= 4 || over <= bestOver) continue;
    const st = getComputedStyle(c).overflowY;
    if (st === 'auto' || st === 'scroll' || st === 'overlay') { best = c; bestOver = over; }
  }
  return best;
}

/** The ring the pad draws around the focused control — a pad has no cursor, so
 *  without this the player cannot tell what A would press.
 *
 *  A DATA ATTRIBUTE, NOT AN INLINE STYLE (2026-09-25): the ring was an inline outline, and clearing it left `style=""`
 *  on the control — the theme's bare-control rules (`button:not([style]):not([class])`, the input / select ones) never
 *  matched it again, so every control the pad had visited fell back to browser styling. `data-ax-focus` is styled by
 *  the theme sheet (lib/ui_theme BASE_CSS). Every other marked element is cleared too: a caller that passes no `prev`
 *  (the shoulder tab walk) left the old ring standing. */
export function markFocus(el: HTMLElement | null, prev: HTMLElement | null): void {
  if (prev && prev !== el) delete prev.dataset.axFocus;
  const doc = (el ?? prev)?.ownerDocument ?? (typeof document !== 'undefined' ? document : null);
  if (doc) for (const o of Array.from(doc.querySelectorAll<HTMLElement>('[data-ax-focus]'))) if (o !== el) delete o.dataset.axFocus;
  if (!el) return;
  el.dataset.axFocus = '';
  try { el.focus({ preventScroll: false }); } catch { /* some elements refuse focus */ }
  el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}
